import { default as Alert } from 'antd/es/alert';
import { default as DatePicker } from 'antd/es/date-picker';
import { default as Modal } from 'antd/es/modal';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { generateRangeFromType } from 'utils/dateRange';
import { API_DATE_FORMAT, DATE_FORMAT, formatDate } from 'utils/dayjs';
import { hasValue } from 'utils/hasValue';

import { useGetCustomerCurrentUser, usePostCollectionOrderReports } from 'api';

import type { DateRangeFilter } from 'reduxLocal/slices/globalFiltersSlice';

import { COLLECTION_ORDER_STATUSES } from 'models/order';

import { useOrganisationId } from 'hooks/context';

import { BlindingDots } from '@atoms/BlindingDots/BlindingDots';
import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { CheckCircleSmallIcon } from '@atoms/Icon/CheckCircleSmallIcon';
import { DownloadThinIcon } from '@atoms/Icon/DownloadThinIcon';
import { RollingLoader } from '@atoms/RollingLoader/RollingLoader';
import { StatusTag } from '@atoms/StatusTag';

import { SelectVirtualAccount } from '@molecules/SelectVirtualAccount';

import {
  COLLECTION_ORDERS_EXPORT_ACCOUNT_TEST_ID,
  COLLECTION_ORDERS_EXPORT_DATE_RANGE_TEST_ID,
  COLLECTION_ORDERS_EXPORT_DIALOG_TEST_ID,
  COLLECTION_ORDERS_EXPORT_SUBMIT_TEST_ID,
  COLLECTION_ORDERS_EXPORT_SUCCESS_TEST_ID
} from './constants';
import type { FiltersState } from './localState';

type PayinOrdersExportDialogProps = {
  open: boolean;
  onClose: () => void;
  filters: FiltersState;
  dateRange: DateRangeFilter;
};

export const PayinOrdersExportDialog = ({
  open,
  onClose,
  filters,
  dateRange
}: PayinOrdersExportDialogProps) => {
  const { data } = useGetCustomerCurrentUser();
  const openNotification = useOpenNotification();

  const currentUserEmail = data ? data.user?.email : 'Loading...';

  const organisationId = useOrganisationId<number>();

  const today = dayjs().endOf('day');
  const { RangePicker } = DatePicker;

  type DateRange = ReturnType<typeof generateRangeFromType>;
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>(
    generateRangeFromType(dateRange)
  );
  const [selectedAccountNumber, setSelectedAccountNumber] = useState<
    string | undefined
  >(filters.accountNumber);

  const {
    mutateAsync: createPayinOrdersReport,
    isSuccess: creationSuccess,
    data: createdReport,
    isPending: isCreationLoading,
    reset: resetDialog
  } = usePostCollectionOrderReports();

  const statusFilter =
    hasValue(filters.status) && filters.status !== 'All'
      ? filters.status
      : undefined;

  const onCreateOrderReportRequest = async (event: {
    preventDefault: () => void;
  }) => {
    event.preventDefault();
    try {
      await createPayinOrdersReport({
        organisationId,
        data: {
          virtual_account_number:
            selectedAccountNumber === 'All' ? undefined : selectedAccountNumber,
          created_after: formatDate(selectedDateRange.from, 'utc', 'iso'),
          created_before: selectedDateRange.to
            ? formatDate(selectedDateRange.to, 'utc', 'iso')
            : dayjs().format(API_DATE_FORMAT),
          ...(hasValue(statusFilter) && { status: statusFilter }),
          ...(hasValue(filters.reason_code_id) && {
            reason_code_id: filters.reason_code_id
          })
        }
      });
    } catch (error: any) {
      onClose();
      if (error?.error?.code === 2001) {
        openNotification({
          kind: 'error',
          message:
            'No orders found for the selected filters. Please adjust your filters and try again.'
        });
      } else {
        openNotification({
          kind: 'error',
          message: 'Something went wrong'
        });
      }
    }
  };

  const onDialogClose = () => {
    resetDialog();
    onClose();
  };

  const invalidDateRange = (startDate: string, endDate: string | undefined) => {
    // date range can't exceed 90 days
    const start = dayjs(startDate, DATE_FORMAT);
    const end = dayjs(endDate, DATE_FORMAT);
    const diff = end.diff(start, 'day') + 1;

    return diff > 90;
  };

  const disableExports = invalidDateRange(
    selectedDateRange.from,
    selectedDateRange.to
  );

  const showDialog = !isCreationLoading && !creationSuccess;
  const showLoading = isCreationLoading && !creationSuccess;
  const showSuccess = creationSuccess && createdReport && !isCreationLoading;

  useEffect(() => {
    setSelectedDateRange(generateRangeFromType(dateRange));
  }, [dateRange]);

  useEffect(() => {
    setSelectedAccountNumber(filters.accountNumber);
  }, [filters.accountNumber]);

  return (
    <Modal
      title={
        <div className="mb-6 flex flex-row">
          <DownloadThinIcon />
          <div className="pl-4">Export pay-ins</div>
        </div>
      }
      open={open}
      styles={{
        mask: {
          backgroundColor: 'var(--tenant-surface-overlay)'
        },
        content: {
          paddingTop: 12,
          backgroundColor: 'rgb(255, 255, 255)'
        }
      }}
      width={336}
      onOk={onDialogClose}
      onCancel={onDialogClose}
      footer={[
        showSuccess ? (
          <PrimaryButton
            key="done"
            onClick={onDialogClose}
            className="w-full"
            size="large"
          >
            Done
          </PrimaryButton>
        ) : (
          <PrimaryButton
            key="export"
            onClick={onCreateOrderReportRequest}
            disabled={disableExports || isCreationLoading}
            data-testid={COLLECTION_ORDERS_EXPORT_SUBMIT_TEST_ID}
            className="w-full"
            size="large"
          >
            Export CSV file
          </PrimaryButton>
        )
      ]}
    >
      {showDialog && (
        <div
          className="my-8 flex flex-col gap-4 rounded-2xl p-0 text-sm"
          data-testid={COLLECTION_ORDERS_EXPORT_DIALOG_TEST_ID}
        >
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-content-secondary">
              Date range
            </label>
            {invalidDateRange(selectedDateRange.from, selectedDateRange.to) && (
              <Alert
                className="h-[30px]"
                message="Please choose less than 90 days"
                type="warning"
                showIcon
              />
            )}
            <RangePicker
              className="h-10 rounded-[20px]"
              data-testid={COLLECTION_ORDERS_EXPORT_DATE_RANGE_TEST_ID}
              format={DATE_FORMAT}
              allowClear={false}
              maxDate={today}
              value={[
                dayjs(selectedDateRange.from, DATE_FORMAT),
                dayjs(selectedDateRange.to, DATE_FORMAT)
              ]}
              onChange={(_, dates) => {
                setSelectedDateRange({
                  type: 'custom',
                  from: dates[0],
                  to: dates[1]
                });
              }}
              placeholder={['Borderless', 'Borderless']}
              variant="outlined"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-content-secondary">
              Account
            </label>
            <SelectVirtualAccount
              className="w-full"
              onChangeCallback={setSelectedAccountNumber}
              currentAccountNumber={selectedAccountNumber}
              e2eId={COLLECTION_ORDERS_EXPORT_ACCOUNT_TEST_ID}
              filterCryptoAccounts={true}
            />
          </div>
          {hasValue(statusFilter) && (
            <div>
              <div className="pb-1 text-xs font-semibold text-content-secondary">
                Status
              </div>
              <div className="flex justify-start font-bold">
                <StatusTag
                  status={statusFilter}
                  statuses={COLLECTION_ORDER_STATUSES}
                />
              </div>
            </div>
          )}
          {hasValue(filters.reason_code_id) && (
            <div>
              <div className="pb-0.5 text-xs font-semibold text-content-secondary">
                Reason code
              </div>
              <span className="font-bold">
                {String(filters.reason_code_id)}
              </span>
            </div>
          )}
        </div>
      )}

      {showLoading && (
        <div className="size-78 flex justify-center gap-4 p-4">
          <RollingLoader ariaLabel="Creating pay-in export" />
          <div className="text-sm">
            <span className="invisible">...</span>
            <span>Processing</span>
            <BlindingDots />
          </div>
        </div>
      )}
      {showSuccess && (
        <div
          className="flex flex-col items-center bg-white"
          data-testid={COLLECTION_ORDERS_EXPORT_SUCCESS_TEST_ID}
        >
          <div className="mx-auto mt-0 flex size-12 items-center justify-center rounded-full">
            <CheckCircleSmallIcon />
          </div>
          <p className="text-center text-lg font-medium">
            Your report is being generated
          </p>
          <div className="px-2 py-4 text-center text-sm">
            When the report is ready you will receive an email
            <br />
            <span className="font-bold text-content-brand">
              {currentUserEmail}
            </span>
            <br />
            with a link where you can download your report.
          </div>
        </div>
      )}
    </Modal>
  );
};
